/**
 * eventLog.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Behavioral event log for AI personalization training.
 *
 * Separate from analytics.js (funnel-events / admin reporting surface).
 * This collection captures the rich behavioral signal that the future
 * personalization layer (compressed user state, vector retrieval,
 * collaborative filter) trains on. Events accumulate now even though
 * the ML layer doesn't yet read them — every week of capture is a
 * week of pre-launch cold-start data accrued.
 *
 * Data location:
 *   userEvents/{uid}/events/{eventId}
 *
 * Why a subcollection (not a flat top-level collection):
 *   - Per-uid query is the dominant access pattern (compressor reads
 *     "last 30 days of events for THIS user").
 *   - GDPR right-to-be-forgotten cascade is a single subcollection
 *     delete instead of a where-clause sweep.
 *   - Firestore rules can use a single owner-based check at the
 *     /userEvents/{uid} level instead of a per-doc owner check.
 *
 * Event schema:
 *   {
 *     type: snake_case event name (see EVENT_TYPES below — locked taxonomy)
 *     ts:   serverTimestamp (write-time canonical)
 *     clientTs: ISO string from the client (for time-zone analysis later)
 *     promptVersion?: string — version tag for AI events
 *     context: { … flexible per type … }
 *     wineId?: string
 *     identityId?: string (when known — for cross-vintage rollups)
 *     orgId?: string (merchant attribution)
 *   }
 *
 * Write semantics:
 *   - Fire-and-forget. A failed event-log write CANNOT block a user
 *     action. Caller awaits nothing; errors swallowed with a warn.
 *   - Server-stamped ts; the clientTs is captured for offline-tolerant
 *     downstream analysis but not authoritative.
 *   - No rate-limiting at the SDK layer (Firestore handles concurrency
 *     internally; the rule layer caps abuse).
 *
 * Retention:
 *   - Default 24 months (configurable later via a TTL field — Firestore
 *     supports TTL policies natively as of 2024).
 *   - User-initiated wipe runs the existing account-deletion cascade
 *     plus this subcollection. Implemented in api/delete-user.js (TODO).
 *
 * Privacy:
 *   - All companion uids stored here are uids the user is connected to.
 *     Anonymization for cross-user aggregation happens at the compressor
 *     layer (sha256-of-uid) — raw events keep uids legible for the
 *     owner's own queries.
 *   - Location is precise (lat/lng + city); the compressor coarsens
 *     before any cross-user aggregation.
 */

import { collection, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore'
import { db } from '../firebase.js'

// ─── Retention configuration ───────────────────────────────────────────────
//
// Default retention is 24 months. Each event gets an `expiresAt` Timestamp
// stamped at write time; Firestore's TTL policy (configured per-collection
// in the Firebase Console) auto-deletes docs once their expiresAt is past.
//
// Users can configure their retention via Preferences → Privacy. App.jsx
// reads profile.eventRetentionMonths on profile snapshot and calls
// setEventRetentionMonths() here to override the default — module-scoped
// variable so logEvent() can read it on each write without a Firestore
// round-trip per event. "Forever" means no expiresAt field on the write
// (Firestore TTL is a no-op for docs without the field).
//
// Allowed values:
//   12  → 1 year
//   24  → 2 years (default)
//   36  → 3 years
//   0   → forever (no expiresAt; never auto-deleted)
//
// Firestore TTL setup (one-time, manual):
//   gcloud firestore fields ttls update --field=expiresAt --collection-group=events --enable-ttl
//   gcloud firestore fields ttls update --field=expiresAt --collection-group=turns  --enable-ttl
//   (or via Firebase Console → Firestore → Indexes → TTL tab)
//
// Once enabled, docs auto-delete within 24h of their expiresAt — the
// Firestore TTL is best-effort, not real-time, but for retention
// compliance (GDPR Article 17) the delete happens within the SLA.
let _retentionMonths = 24
export function setEventRetentionMonths(months) {
  if (months === 0 || months === 12 || months === 24 || months === 36) {
    _retentionMonths = months
  }
}
function computeExpiresAt() {
  if (_retentionMonths === 0) return null   // "forever" — no TTL field written
  const ms = _retentionMonths * 30 * 24 * 60 * 60 * 1000   // approximate months → ms
  return Timestamp.fromDate(new Date(Date.now() + ms))
}

// ─── Locked event taxonomy ──────────────────────────────────────────────────
//
// Adding a new type: append, don't rename. Existing types are forever
// because downstream queries / analytics dashboards pin to them.
//
// Each type listed with: a one-line description + the context payload
// shape callers should pass. Schema is documented, not enforced — the
// Firestore rule only validates uid + structural fields (type, ts).
export const EVENT_TYPES = Object.freeze({
  // ── Wine lifecycle ────────────────────────────────────────────────────────
  add_wine:           'A wine was added to the cellar',
  scan_label:         'Label was photographed + extracted via OCR/AI',
  rate_wine:          'A score was saved on a wine',
  uncork_wine:        'A wine was uncorked (consumed)',

  // ── AI Sommelier interaction ──────────────────────────────────────────────
  sommelier_chat:        'User sent a message in Ask Vinarai',
  sommelier_thumbs_up:   'User upvoted an AI response',
  sommelier_thumbs_down: 'User downvoted an AI response',

  // ── Wine of the Day funnel ────────────────────────────────────────────────
  wotd_shown:     'WOTD was rendered',
  wotd_picked:    'User picked the suggested WOTD',
  wotd_dismissed: 'User explicitly dismissed the WOTD',

  // ── Recommendation funnel ─────────────────────────────────────────────────
  recommend_shown:   'A recommendation surface was viewed',
  recommend_clicked: 'User tapped into a recommendation',
  recommend_ignored: 'A recommendation was shown but not interacted with',

  // ── Dine Out funnel ───────────────────────────────────────────────────────
  dine_out_query: 'User submitted a wine-list photo for pairing',
  dine_out_pick:  'User picked a wine from the AI Dine Out result',

  // ── Tasting flow ──────────────────────────────────────────────────────────
  tasting_started:    'User entered the guided tasting flow (chose a mode)',
  tasting_stage_done: 'User advanced past a stage of the tasting flow',
  tasting_complete:   'User completed the structured CMS-A tasting flow',
  tasting_skipped:    'User skipped a tasting flow they entered',
  tasting_retry:      'User tapped Try Again after completing or erroring',
  tasting_rank_up:    'User\'s tasting rank advanced to a new tier',

  // ── Sharing / social ──────────────────────────────────────────────────────
  share_wine:    'User shared a wine via the share sheet',
  accept_invite: 'User accepted a household/friend invite',

  // ── Session ───────────────────────────────────────────────────────────────
  session_start:  'First load of a session',
  session_resume: 'Returning visit within the same session window',

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  // Funnel-level lifecycle events. analytics.js still writes the canonical
  // funnel rows to analyticsEvents/* for admin reporting; these mirrors
  // exist so the personalization compressor sees the same signal in one
  // place. Onboarding-step events stay analytics-only (the compressor
  // doesn't need per-slide granularity).
  account_created:     'User signed up — first auth event on this account',
  onboarding_complete: 'User finished the onboarding quiz (mirror of analytics)',

  // ── Payment / billing (Stripe-driven; server-write only for the
  //     post-checkout events — client writes the click-side ones) ────────────
  payment_upgrade_clicked:    'User clicked an upgrade CTA',
  payment_subscribe_start:    'Stripe Checkout session created',
  payment_subscribe_complete: 'Stripe webhook confirmed a subscription',
  payment_interval_switch:    'User switched monthly↔annual',
  payment_downgrade:          'Subscription tier reduced',
  payment_churn:              'Subscription cancelled or expired',
})

/**
 * Log a behavioral event for the current user. Fire-and-forget.
 *
 *   logEvent('uncork_wine', {
 *     uid: user.uid,
 *     wineId: 'abc123',
 *     identityId: 'inkster_2018',
 *     context: { score: 92, occasion: 'date', companions: ['uid2'] },
 *   })
 *
 * @param {string} type — must be one of EVENT_TYPES keys
 * @param {object} opts
 * @param {string} opts.uid — required
 * @param {string} [opts.wineId]
 * @param {string} [opts.identityId]
 * @param {string} [opts.orgId]
 * @param {string} [opts.promptVersion]
 * @param {object} [opts.context] — type-specific payload
 */
export function logEvent(type, opts = {}) {
  if (!type || !(type in EVENT_TYPES)) {
    console.warn('[eventLog] unknown event type:', type)
    return
  }
  const { uid, wineId, identityId, orgId, promptVersion, context } = opts
  if (!uid) {
    console.warn('[eventLog] no uid passed for', type)
    return
  }
  const payload = {
    type,
    ts:       serverTimestamp(),
    clientTs: new Date().toISOString(),
  }
  // Retention TTL — stamps expiresAt unless retention is set to "forever"
  // (0). Firestore TTL policy (configured per-collection in console)
  // auto-deletes docs once expiresAt is past. See setEventRetentionMonths
  // + computeExpiresAt at the top of this file.
  const expiresAt = computeExpiresAt()
  if (expiresAt) payload.expiresAt = expiresAt
  if (wineId)        payload.wineId = wineId
  if (identityId)    payload.identityId = identityId
  if (orgId)         payload.orgId = orgId
  if (promptVersion) payload.promptVersion = promptVersion
  // Sanitize context defensively. Firestore REJECTS the entire write
  // when ANY field value is `undefined`, and many callers pass
  // computed/derived fields that may be undefined at the call site
  // (e.g. `context: { name: wine.name, mode: blindMode ? 'blind' :
  // 'practice' }` where wine.name was null or blindMode was an
  // unresolved boolean state). Pre-fix, a single undefined caused
  // the entire event-log write to throw, which surfaced as a noisy
  // error in the user's console AND in mobile bug reports as "save
  // failed" (when the actual wine save succeeded — only the event
  // log failed).
  //
  // Strip undefined values + drop empty/non-object contexts. Cheaper
  // than auditing every call site for missing fields.
  if (context && typeof context === 'object') {
    const sanitized = {}
    for (const [k, v] of Object.entries(context)) {
      if (v !== undefined) sanitized[k] = v
    }
    if (Object.keys(sanitized).length > 0) payload.context = sanitized
  }

  // Write to /userEvents/{uid}/events/{auto-id}.
  // addDoc returns a Promise — we don't await; a failed write must
  // never block the user flow.
  addDoc(collection(db, 'userEvents', uid, 'events'), payload).catch(err => {
    // Use warn not error — these are non-critical. Sentry captures
    // happen only at much higher abstraction levels (e.g. AddWine save
    // catch). A noisy log on every event-log failure would flood the
    // error reporter on offline-then-online client transitions.
    console.warn(`[eventLog] ${type} write failed:`, err?.code || err?.message)
  })
}
